import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { ONBOARDING_API_CONFIG, OnboardingApiConfigInterface } from '@planone/bau365/data-access-onboarding';
import {
  MOCK_API,
  MockEnvironmentInterface,
  ProductMockGenerator,
} from '@planone/bau365/util-api-response-mock-generators';
import { Product } from '@planone/bau365/util-fair-data';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { concatMap, map, Observable, of } from 'rxjs';

import { ProductResponse } from '../../interfaces/product.interfaces';

@Injectable()
export class DataAccessProductsService {
  constructor(
    private httpClient: HttpClient,
    @Inject(LOCALE_ID) private locale: string,
    @Inject(ONBOARDING_API_CONFIG)
    private onboarding_api_config: OnboardingApiConfigInterface,
    @Inject(MOCK_API) private mockConfig: MockEnvironmentInterface,
    private togglesFacade: DataAccessTogglesFacade
  ) {}

  private static emptyArrayToUndefined<T>(inputArray: T[] | null | undefined): T[] | undefined {
    if (!inputArray) return undefined;

    const filteredArray = inputArray.filter((value: T) => !!value);

    if (filteredArray.length <= 0) return undefined;

    return filteredArray;
  }

  private static transformProduct(product: Product, showContacts: boolean): Product {
    return {
      ...product,
      key_facts: this.emptyArrayToUndefined(product.key_facts),
      countries: this.emptyArrayToUndefined(product.countries),
      downloads: this.emptyArrayToUndefined(product.downloads),
      videos: this.emptyArrayToUndefined(product.videos),
      links: this.emptyArrayToUndefined(product.links),
      contact_persons: showContacts ? this.emptyArrayToUndefined(product.contact_persons) : undefined,
    };
  }

  getProducts(fairId: string, cursor: string, limit: number) {
    if (this.mockConfig.globally) {
      return of(ProductMockGenerator.getPaginatedList());
    }

    return this.httpClient
      .get<ProductResponse>(`${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/products`, {
        params: {
          cursor: cursor,
          limit: limit,
        },
      })
      .pipe(
        map((response) => ({
          ...response,
          items: response.items.map((product: Product) => DataAccessProductsService.transformProduct(product, true)),
        }))
      );
  }

  getProduct(fairId: string, productId: string) {
    if (this.mockConfig.globally) {
      return of(ProductMockGenerator.getObject(productId, undefined));
    }

    return this.httpClient
      .get<Product>(`${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/products/${productId}`)
      .pipe(map((product) => DataAccessProductsService.transformProduct(product, true)));
  }

  getProductsByIds(fairId: string, ids: string[] | undefined) {
    if (this.mockConfig.globally) {
      return of(ProductMockGenerator.getList(ids));
    }

    if (!ids) {
      return <Observable<Product[]>>of([]);
    }

    let observable: Observable<Product[]> = of([]);

    ids.forEach((id: string) => {
      observable = observable.pipe(
        concatMap((list) => this.getProduct(fairId, id).pipe(map((product: Product) => list.concat([product]))))
      );
    });

    return observable;
  }

  getLandingPageObjects(ids: string[] | undefined) {
    if (this.mockConfig.globally) {
      return of(ProductMockGenerator.getList(ids, undefined));
    }

    return this.httpClient
      .post<Product[]>(`${this.onboarding_api_config.fairDataGatewayUrl}/landing/products`, ids, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(
        map((products: Product[]) =>
          products.map((product: Product) => DataAccessProductsService.transformProduct(product, false))
        )
      );
  }
}
